// @file The store for layout pickers displayed by DashLayoutPickerPage.vue and LayoutPickerDesktop.vue
import { trackEvent } from '@@/bits/analytics'
import { setCookie } from '@@/bits/cookie'
import device from '@@/bits/device'
import { captureException, captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { getAndClearSearchParam, navigateTo } from '@@/bits/location'
import safeStorage from '@@/bits/safe_storage'
import { displayNameForUser } from '@@/bits/user_model'
import { unboundedWatch } from '@@/bits/vue'
import { LibrarySubscriptionStatus, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type { LibraryIdToUserRoleMap } from '@@/vuexstore/modules/accounts'
import type { JsonAPIResource } from '@padlet/arvo'
import { __ } from 'bits/intl'
import { WallTemplate as WallTemplateApi } from 'dashboard/padlet_api'
import { LibraryMembershipRole, LibraryType } from 'enums'
import { defineStore } from 'pinia'
import { useDashboardStore } from 'pinia/dashboard'
import PadletApi from 'surface/padlet_api'
import type {
  AccountKey,
  Library,
  LibraryAccess,
  LibraryId,
  User,
  UserOptions,
  WallCamelCase as Wall,
  WallCreationFromTemplateResult,
  WallLayoutAttributes,
  WallLayoutPreviewImage,
  WallTemplate,
} from 'types'
import { computed, ref } from 'vue'

enum WallTemplatesStatus {
  Loading = 'Loading',
  Completed = 'Completed',
  Errored = 'Errored',
}

export const useLayoutPickerStore = defineStore('layoutPicker', () => {
  const dashboardStore = useDashboardStore()
  const wallCreatableLibraries = computed((): Library[] => dashboardStore.wallCreatableLibraries)
  const wallViewableLibrariesArray = computed((): Library[] => dashboardStore.wallViewableLibrariesArray)
  const currentUser = computed((): User => dashboardStore.currentUser)
  const currentUserOptions = computed((): UserOptions => dashboardStore.currentUserOptions)
  const currentAccountKey = computed((): AccountKey | null => dashboardStore.currentAccountKey)
  const userRolesByLibraryId = computed((): LibraryIdToUserRoleMap => dashboardStore.userRolesByLibraryId)
  const defaultLibrary = computed((): Library | undefined => dashboardStore.defaultLibrary)
  const isActiveCollectionCrossLibrary = computed((): boolean => dashboardStore.isActiveCollectionCrossLibrary)
  const globalSnackbarStore = useGlobalSnackbarStore()

  // State
  const isCreatingWall = ref(false)
  const errorCreatingWall = ref(false)
  const xLibrarySelectionMenu = ref(false)
  const librarySelected = ref<Library | null>(null)
  const lastLibrarySelected = ref<Library | null>(null)
  const xLayoutPicker = ref(false)
  const isSimpleSectionExpanded = ref(true) // always expand the simple section
  const isAdvancedSectionExpanded = ref<boolean>(
    safeStorage.getItem('isAdvancedSectionExpanded') !== null
      ? JSON.parse(safeStorage.getItem('isAdvancedSectionExpanded') as string)
      : true,
  )
  const isWallTemplatesExpanded = ref(true)
  const simpleWallLayouts = ref<WallLayoutAttributes[]>([])
  const advancedWallLayouts = ref<WallLayoutAttributes[]>([])
  const previewImageStack = ref<WallLayoutPreviewImage[]>([])

  const currentActiveCollectionWalls = ref<Wall[]>([])
  const wallTemplates = ref<WallTemplate[]>([])
  const wallTemplateStatus = ref(WallTemplatesStatus.Loading)
  const galleryTemplates = ref<WallTemplate[]>([])
  const personalLibraryWallTemplates = ref<WallTemplate[]>([])
  const nonPersonalLibraryWallTemplatesByLibraryId = ref<Record<LibraryId, WallTemplate[]>>({})
  const isGallerySectionExpanded = ref<boolean>(
    safeStorage.getItem('isGallerySectionExpanded') !== null
      ? JSON.parse(safeStorage.getItem('isGallerySectionExpanded') as string)
      : true,
  )

  // Getters
  const shortcutWallLayouts = computed((): WallLayoutAttributes[] => {
    const wallFormat = simpleWallLayouts.value.find((format) => format.value === 'grid')
    const wallWithSectionsFormat = advancedWallLayouts.value.find((format) => format.value === 'grid')
    if (wallFormat === undefined) return []
    if (wallWithSectionsFormat === undefined) return [wallFormat]
    return [wallFormat, wallWithSectionsFormat]
  })
  const previewImage = computed(
    (): WallLayoutPreviewImage => previewImageStack.value[previewImageStack.value.length - 1],
  )
  const showPreview = computed(() => previewImageStack.value.length !== 0)
  const isSelectedLibrarySchool = computed(() => librarySelected.value?.libraryType === LibraryType.School)
  const isSelectedLibraryClassroom = computed(() => librarySelected.value?.libraryType === LibraryType.Classroom)
  const isSelectedLibraryTeam = computed(() => librarySelected.value?.libraryType === LibraryType.Team)
  const selectedNotCreatableLibrary = computed(
    (): boolean =>
      !(librarySelected.value == null) && !wallCreatableLibraries.value.includes(librarySelected.value as Library),
  )
  const cannotMakeWallBecauseOfTeamLibraryPermissions = computed(
    (): boolean => isSelectedLibraryTeam.value && selectedNotCreatableLibrary.value,
  )
  const cannotMakeWallBecauseOfSchoolLibraryPermissions = computed(
    (): boolean => isSelectedLibrarySchool.value && selectedNotCreatableLibrary.value,
  )
  const cannotMakeWallBecauseOfClassroomLibraryPermissions = computed(
    (): boolean => isSelectedLibraryClassroom.value && selectedNotCreatableLibrary.value,
  )
  const cannotMakeWallBecauseOfTenantPermissions = computed(
    (): boolean => currentUserOptions.value.canMakeInTenant === false,
  )
  const currentLibrarySubscriptionStatus = computed((): LibrarySubscriptionStatus | null => {
    if (librarySelected.value === null || librarySelected.value === undefined) return null
    if (librarySelected.value.isDeprecatedFreeTier) return null
    if (librarySelected.value.membershipTier === 'gold') return LibrarySubscriptionStatus.Paying
    if (librarySelected.value.membershipTier === 'trial') return LibrarySubscriptionStatus.OnTrial
    if (librarySelected.value.isPlanCancelled) return LibrarySubscriptionStatus.Cancelled
    if (librarySelected.value.isTrialExpired as boolean) return LibrarySubscriptionStatus.TrialExpired
    return null
  })
  const atQuota = computed(
    (): boolean =>
      (librarySelected.value == null && !currentUser.value?.quota?.can_make) ||
      (!(librarySelected.value == null) && librarySelected.value.quota?.quotaHit),
  )

  const xUpgradeButton = computed((): boolean => {
    if (cannotMakeWallBecauseOfTenantPermissions.value) {
      return false
    }

    return (
      librarySelected.value == null ||
      userRolesByLibraryId.value[librarySelected.value?.id] === LibraryMembershipRole.Owner
    )
  })

  const cannotMakeWall = computed(
    (): boolean =>
      selectedNotCreatableLibrary.value ||
      cannotMakeWallBecauseOfTenantPermissions.value ||
      atQuota.value ||
      currentLibrarySubscriptionStatus.value === LibrarySubscriptionStatus.Cancelled ||
      currentLibrarySubscriptionStatus.value === LibrarySubscriptionStatus.TrialExpired,
  )

  const unableToCreateWallText = computed((): string => {
    // Cannot make walls because of permissions
    if (cannotMakeWallBecauseOfTeamLibraryPermissions.value) {
      return __(
        "You do not have Maker permissions in this team. <br> If you'd like to make padlets please ask an Admin to change your role.",
      )
    }

    if (cannotMakeWallBecauseOfSchoolLibraryPermissions.value || cannotMakeWallBecauseOfTenantPermissions.value) {
      return __('An admin has disabled permissions for making new padlets.')
    }

    if (cannotMakeWallBecauseOfClassroomLibraryPermissions.value) {
      return __('A teacher has disabled permissions for making new padlets.')
    }

    // At quota for personal libraries (accounts)
    if (librarySelected.value == null) {
      return __('You’ve reached your padlet quota. Upgrade to continue making padlets.')
    }

    /**
     * For trial libraries (team and classroom), we don't limit the quota, so there's no need to check for quota
     */

    const currentUserLibraryRole = userRolesByLibraryId.value[librarySelected.value.id]

    // Trial expired
    if (currentLibrarySubscriptionStatus.value === LibrarySubscriptionStatus.TrialExpired) {
      if (isSelectedLibraryClassroom.value) {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your classroom’s free trial has ended. Upgrade to continue making new padlets.')
        } else if (currentUserLibraryRole === LibraryMembershipRole.Teacher) {
          // We want to show the string "the classroom owner" for classroom teachers for clarity
          return __(
            'Your classroom’s free trial has ended. Ask the classroom owner to upgrade to continue making padlets.',
          )
        } else {
          return __('Your classroom’s free trial has ended. Ask a teacher to upgrade to continue making padlets.')
        }
      } else {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your team’s free trial has ended. Upgrade to continue making new padlets.')
        } else {
          return __('Your team’s free trial has ended. Ask the team owner to upgrade to continue making padlets.')
        }
      }
    }

    // Subscription cancelled
    if (currentLibrarySubscriptionStatus.value === LibrarySubscriptionStatus.Cancelled) {
      if (isSelectedLibraryClassroom.value) {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your classroom’s subscription has been cancelled. Upgrade to continue making new padlets.')
        } else if (currentUserLibraryRole === LibraryMembershipRole.Teacher) {
          // We want to show the string "the classroom owner" for classroom teachers for clarity
          return __(
            'Your classroom’s subscription has been cancelled. Ask the classroom owner to upgrade to continue making padlets.',
          )
        } else {
          return __(
            'Your classroom’s subscription has been cancelled. Ask a teacher to upgrade to continue making padlets.',
          )
        }
      } else {
        if (currentUserLibraryRole === LibraryMembershipRole.Owner) {
          return __('Your team’s subscription has been cancelled. Upgrade to continue making new padlets.')
        } else {
          return __(
            'Your team’s subscription has been cancelled. Ask the team owner to upgrade to continue making padlets.',
          )
        }
      }
    }

    /**
     * For non-trial libraries.
     */

    // At quota school libraries
    if (isSelectedLibrarySchool.value) {
      return currentUserLibraryRole === LibraryMembershipRole.Owner
        ? __('Your school has reached its padlet quota. Upgrade to continue making padlets.')
        : __('Your school has reached its padlet quota. Ask the school owner to upgrade to continue making padlets.')
    }

    // At quota for team libraries
    if (isSelectedLibraryTeam.value) {
      return currentUserLibraryRole === LibraryMembershipRole.Owner
        ? __('Your team has reached its padlet quota. Upgrade to continue making padlets.')
        : __('Your team has reached its padlet quota. Ask the team owner to upgrade to continue making padlets.')
    }

    // At quota for personal libraries (accounts)
    return __('You’ve reached your padlet quota. Upgrade to continue making padlets.')
  })
  const emptyLibraryAccess: LibraryAccess = {
    canViewLibraryInfo: false,
    canUpdateLibraryInfo: false,
    canUpdatePermissions: false,
    canViewLibraryMemberships: false,
    canManageMembers: false,
    canManageBilling: false,
    canTrashLibrary: false,
    canUseLibraryAsOwner: false,
    canUseLibraryAsAdmin: false,
    canUseLibraryAsTeacher: false,
    canViewLibrarySecurity: false,
    canInviteSchoolAdmins: false,
    canInviteSchoolTeachers: false,
    canInviteSchoolStudents: false,
    canInviteMembers: false,
    canViewGallery: false,
    canManageContentSafetySettings: false,
  }
  const currentLibrary = computed((): Partial<Library> => {
    return {
      name: librarySelected.value != null ? librarySelected.value.name : displayNameForUser(currentUser.value),
      avatar: librarySelected.value != null ? librarySelected.value.avatar : currentUser.value.avatar,
      libraryType: librarySelected.value != null ? librarySelected.value.libraryType : LibraryType.Solo,
      libraryAccess: librarySelected.value != null ? librarySelected.value.libraryAccess : emptyLibraryAccess,
    }
  })

  const isLoadingWallTemplates = computed((): boolean => wallTemplateStatus.value === WallTemplatesStatus.Loading)
  const isCompletedWallTemplates = computed((): boolean => wallTemplateStatus.value === WallTemplatesStatus.Completed)
  const currentLibraryWallTemplates = computed((): WallTemplate[] => {
    return librarySelected.value === null
      ? personalLibraryWallTemplates.value
      : nonPersonalLibraryWallTemplatesByLibraryId.value[librarySelected.value.id]
  })

  // Actions
  function initializeState({
    wallLayoutSections,
    currentUserWallTemplateIds,
    libraryToWallTemplateIds,
    dashboardGalleryTemplates,
  }: {
    wallLayoutSections: {
      simple: WallLayoutAttributes[]
      advanced: WallLayoutAttributes[]
    }
    currentUserWallTemplateIds: WallTemplate[]
    libraryToWallTemplateIds: Record<LibraryId, WallTemplate[]>
    dashboardGalleryTemplates: WallTemplate[]
  }): void {
    simpleWallLayouts.value = wallLayoutSections.simple
    advancedWallLayouts.value = wallLayoutSections.advanced
    personalLibraryWallTemplates.value = currentUserWallTemplateIds
    nonPersonalLibraryWallTemplatesByLibraryId.value = libraryToWallTemplateIds
    galleryTemplates.value = dashboardGalleryTemplates
    setInitialLibrarySelected()
  }

  void unboundedWatch(wallViewableLibrariesArray, () => {
    // Re-set the initial library when the wallViewableLibrariesArray is fetched fully
    // Context: when you first open the layout picker (especially visit the mobile layout picker), the wallViewableLibrariesArray is not fetched yet. So the initial library is not set correctly
    setInitialLibrarySelected()
  })

  function setInitialLibrarySelected(): void {
    // Show the corresponding library if the user is in a library view on the dashboard
    if (currentAccountKey.value?.type === 'library') {
      const currentlyOpenLibrary = wallViewableLibrariesArray.value.find(
        (library) => library.id === currentAccountKey.value?.id,
      )
      if (currentlyOpenLibrary === undefined) return
      librarySelected.value = currentlyOpenLibrary
    } else if (
      isAppUsing('dashboardSetting') &&
      isActiveCollectionCrossLibrary.value &&
      defaultLibrary.value !== undefined
    ) {
      // Show the default library on cross collection views, e.g. Recents, Bookmarks, Shared, and Gallery.
      librarySelected.value = defaultLibrary.value
    }
  }

  function openPreview(payload: { previewImage: WallLayoutPreviewImage }): void {
    previewImageStack.value.push(payload.previewImage)
  }

  function closePreview(): void {
    previewImageStack.value.pop()
  }

  // Processing and retry stages
  async function createWall({ viz, groupBySection }: { viz: string; groupBySection?: boolean }): Promise<void> {
    isCreatingWall.value = true
    previewImageStack.value = []
    try {
      const response = await PadletApi.Wall.create({ viz, libraryId: librarySelected.value?.id, groupBySection })
      // To show the onboarding flow when user first visit the padlet
      setCookie('first_view', 'true', { path: `/${response.address as string}` })
      navigateTo(response.links.show as string, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        closeLayoutPicker()
      }
    } catch (e) {
      captureException(e)
      isCreatingWall.value = false
      errorCreatingWall.value = true
    }
  }

  async function createGridWithoutSectionsWall(): Promise<void> {
    await createWall({ viz: 'grid', groupBySection: false })
  }

  async function createWallFromTemplate(wallTemplate: WallTemplate): Promise<void> {
    isCreatingWall.value = true
    try {
      // Format for template title is `wall-title - August 23, 2023`
      const currentDateLongFormat = new Date().toLocaleDateString('default', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      })
      const response = await PadletApi.Wall.createFromTemplate(wallTemplate.id, {
        jsonData: {
          title: `${wallTemplate.title} - ${currentDateLongFormat}`,
          description: wallTemplate.description,
          with_posts: true,
          with_same_privacy: false,
          with_original_authors: false,
          library_id: librarySelected.value?.id ?? null,
        },
      })
      const wallCreatedFromTemplateUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)?.attributes
        .url
      navigateTo(wallCreatedFromTemplateUrl, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        closeLayoutPicker()
      }
    } catch (e) {
      captureException(e)
      isCreatingWall.value = false
      errorCreatingWall.value = true
    }
  }

  // Similar to createWallFromTemplate, however with different jsonData.
  // TODO: update WallProps for onboarding upon click.
  async function createWallFromGalleryTemplate(wallTemplate: WallTemplate): Promise<void> {
    isCreatingWall.value = true
    try {
      const response = await PadletApi.Wall.createFromTemplate(wallTemplate.id, {
        jsonData: {
          title: `${wallTemplate.title}`,
          description: wallTemplate.description,
          with_posts: true,
          with_same_privacy: false,
          with_original_authors: false,
          library_id: librarySelected.value?.id ?? null,
        },
      })
      const wallCreatedFromTemplateUrl = (response?.data as JsonAPIResource<WallCreationFromTemplateResult>)?.attributes
        .url
      navigateTo(wallCreatedFromTemplateUrl, { target: device.electronApp ? '_blank' : '_self' })
      if (device.electronApp) {
        closeLayoutPicker()
      }
    } catch (e) {
      captureException(e)
      isCreatingWall.value = false
      errorCreatingWall.value = true
    }
  }

  async function fetchWallTemplates(): Promise<void> {
    wallTemplateStatus.value = WallTemplatesStatus.Loading
    try {
      const response = await WallTemplateApi.fetchWallTemplates(
        librarySelected.value === null ? undefined : librarySelected.value.id,
      )
      const data = response.data as Array<JsonAPIResource<WallTemplate>>
      wallTemplates.value = data.map((wallTemplate) => wallTemplate.attributes)
      wallTemplateStatus.value = WallTemplatesStatus.Completed
    } catch (e) {
      void globalSnackbarStore.setSnackbar({
        message: 'Error fetching wall templates',
        notificationType: SnackbarNotificationType.error,
      })
      captureFetchException(e, { source: 'LayoutPickerFetchWallTemplates' })
      wallTemplateStatus.value = WallTemplatesStatus.Errored
    }
  }

  function openLayoutPicker(): void {
    setInitialLibrarySelected()
    xLayoutPicker.value = true
    // Log when a user opens the layout picker
    trackEvent('Dashboard', 'Opened layout picker')

    if (!isAppUsing('templatesWithNewSidebar')) {
      void fetchWallTemplates()
    }
  }

  function closeLayoutPicker(): void {
    getAndClearSearchParam('create')
    xLayoutPicker.value = false
    lastLibrarySelected.value = librarySelected.value
    librarySelected.value = null
    previewImageStack.value = []
    if (isCreatingWall.value) {
      isCreatingWall.value = false
    }
    errorCreatingWall.value = false
  }

  function reopenPickerAfterWallCreationError(): void {
    isCreatingWall.value = !isCreatingWall.value
    errorCreatingWall.value = !errorCreatingWall.value
  }

  function toggleLibrarySelectionMenu(): void {
    xLibrarySelectionMenu.value = !xLibrarySelectionMenu.value
  }

  function updateLibrarySelected(payload: { librarySelected: Library; user: User }): void {
    librarySelected.value = payload.librarySelected
    xLibrarySelectionMenu.value = !xLibrarySelectionMenu.value

    if (!isAppUsing('templatesWithNewSidebar')) {
      void fetchWallTemplates()
    }
  }

  function toggleAdvancedSection(value: boolean): void {
    isAdvancedSectionExpanded.value = value
    // Persist the state of the advanced section to decide whether to expand it or not in the next time the user opens the layout picker
    safeStorage.setItem('isAdvancedSectionExpanded', JSON.stringify(isAdvancedSectionExpanded.value))
  }

  function toggleGallerySection(value: boolean): void {
    isGallerySectionExpanded.value = value
    // Persist the state of the gallery section to decide whether to expand it or not in the next time the user opens the layout picker
    safeStorage.setItem('isGallerySectionExpanded', JSON.stringify(isGallerySectionExpanded.value))
  }

  return {
    // State
    showPreview,
    isCreatingWall,
    errorCreatingWall,
    xLibrarySelectionMenu,
    currentLibrary,
    librarySelected,
    lastLibrarySelected,
    xLayoutPicker,
    isSimpleSectionExpanded,
    isAdvancedSectionExpanded,
    isWallTemplatesExpanded,
    simpleWallLayouts,
    advancedWallLayouts,
    previewImageStack,
    currentActiveCollectionWalls,
    galleryTemplates,

    // Getters
    shortcutWallLayouts,
    previewImage,
    selectedNotCreatableLibrary,
    cannotMakeWallBecauseOfSchoolLibraryPermissions,
    cannotMakeWallBecauseOfTeamLibraryPermissions,
    currentLibrarySubscriptionStatus,
    cannotMakeWallBecauseOfTenantPermissions,
    atQuota,
    xUpgradeButton,
    cannotMakeWall,
    unableToCreateWallText,
    isSelectedLibrarySchool,
    isSelectedLibraryClassroom,
    isSelectedLibraryTeam,
    wallTemplates,
    isLoadingWallTemplates,
    isCompletedWallTemplates,
    currentLibraryWallTemplates,
    isGallerySectionExpanded,

    // Actions
    initializeState,
    setInitialLibrarySelected,
    openPreview,
    closePreview,
    openLayoutPicker,
    closeLayoutPicker,
    createWall,
    createWallFromTemplate,
    createWallFromGalleryTemplate,
    createGridWithoutSectionsWall,
    reopenPickerAfterWallCreationError,
    toggleLibrarySelectionMenu,
    updateLibrarySelected,
    toggleAdvancedSection,
    fetchWallTemplates,
    toggleGallerySection,
  }
})
